import _ from 'lodash'
import { FormGroup, Radio, RadioItem, Text } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC, ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { AButton, ARouterLink } from 'views/components/Analytics'
import { ResponsiveModal } from 'views/components/ResponsiveModal/ResponsiveModal'
import { SharedModalContent, SharedModalInputs } from 'views/components/SharedModal'
import { AddSampleRequestFields } from 'views/components/Sample/SampleRequest/AddSampleRequestModal'
import { CommonFormFields } from 'views/components/Sample/SampleRequest/CommonFormFields'
import { AnalysisTypes, LaboratoryCodes } from 'views/pages/Samples/types'
import { SharedModalSecondContent } from '../SharedModal/styled'
import { HrWithHeader } from '../Hr/Hr'
import { FormItemWrapper } from 'views/styled/common'
import { Card } from '../Card/Card'
import { useDealContext } from 'views/pages/Deal/DealDetail/DealContext'
import React from 'react'
import { generatePath } from 'react-router'
import SamplesRoutes from 'views/pages/Samples/routes'

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 16px;
  margin-top: 24px;
  justify-content: space-between;
`

const ButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  column-gap: 16px;
`

const TextLink: FC<{ children?: ReactNode }> = ({ children }) => (
  <ARouterLink eventName={'SampleLink'} to={''}>
    <Text typography="bodyMedium" as="span">
      {children}
    </Text>
  </ARouterLink>
)

type ModalFields = AddSampleRequestFields & { seller_overload: boolean; submitMode: 'full' | 'overload' }

type Props = {
  onClose: () => void
  changeDealStatusHandler: () => Promise<unknown> | void
  //changeDealStatusProgress?: Progress
}

export const InProgressModal: FC<Props> = ({ onClose, changeDealStatusHandler }) => {
  const { deal, samplesInfo } = useDealContext()
  const { t } = useTranslation(['deal', 'common', 'address', 'samples'])
  const { formProgress, formHandler } = useAFormHandler()
  const samplesData = samplesInfo.data?.map((s, i) => (
    <React.Fragment key={s.id}>
      {i > 0 && '; '}
      <ARouterLink
        eventName={'SampleLink'}
        target="_blank"
        to={generatePath(SamplesRoutes.Details, {
          id: s.id,
        })}
      >
        <Text typography="bodyMedium" as="span">
          {s.title}
        </Text>
      </ARouterLink>
      <TextLink></TextLink>
    </React.Fragment>
  ))
  const handleChangeDealStatus = async () => {
    await changeDealStatusHandler()
    onClose()
  }

  const formik = useFormik<ModalFields>({
    initialValues: {
      bid: deal.sale_bid.id,
      deal: deal.id,
      senior_regional: null,
      quantity: deal.quantity,
      product: deal.product.id,
      seller_user: deal.sale_bid.owner.id,
      seller_address: deal.sale_bid.address.id,
      phone: deal.sale_bid.owner.phone,
      laboratory: LaboratoryCodes.voronezh,
      analysis_type: AnalysisTypes.gost,
      lab_comment: null,
      lab_assistant: null,
      comment: null,
      submitMode: 'full',
      seller_overload: false,
    },

    onSubmit: formHandler(
      async () => {
        await apiClient.patch(endpoints.setOverload(parseInt(deal.id)), {
          seller_overload: formik.values.seller_overload,
        })
        if (formik.values.submitMode === 'full') {
          await apiClient.post(endpoints.sampleRequest(), _.omit(formik.values, ['submitMode', 'seller_overload']))
        }
      },
      {
        onSuccess: async () => {
          Promise.all([handleChangeDealStatus(), samplesInfo.refetch(true)])
          onClose()
        },
        onError: (error) => {
          const { errors } = error
          formik.setErrors(errors)
        },
      },
    ),
  })

  const customFooterJSX = (
    <ButtonsWrapper>
      <ButtonsGroup>
        <AButton
          id="sendFullData"
          size="big"
          progress={formProgress}
          onClick={async () => {
            await formik.setFieldValue('submitMode', 'full')
            formik.submitForm()
          }}
        >
          {t('common:save')}
        </AButton>
        <AButton color="secondary" size="big" onClick={onClose} id="cancelChangeDealStatus">
          {t('common:canceling')}
        </AButton>
      </ButtonsGroup>
      <AButton
        id="sendOverloadData"
        size="big"
        progress={formProgress}
        color="secondary"
        onClick={async () => {
          await formik.setFieldValue('submitMode', 'overload')
          formik.submitForm()
        }}
      >
        {t('accordion.sample.sendWithoutRequest')}
      </AButton>
    </ButtonsWrapper>
  )

  return (
    <ResponsiveModal
      id="moveDealToInProgress"
      title={t('ToInProgress')}
      progress={formProgress}
      onConfirm={formik.submitForm}
      onClose={onClose}
      size="large"
      customFooter={customFooterJSX}
    >
      <SharedModalContent>
        <HrWithHeader text={t('DCOverloadTitle')} />
        <SharedModalSecondContent>
          <SharedModalInputs>
            <FormGroup label={t('address:fields.overload')} error={formik.errors.seller_overload}>
              <FormItemWrapper>
                <Radio
                  value={formik.values.seller_overload}
                  onChange={(seller_overload) => formik.setFieldValue('seller_overload', seller_overload)}
                >
                  <RadioItem value={false} label={t('address:overLoadTypes.normal')} />
                  <RadioItem value={true} label={t('address:overLoadTypes.full')} />
                </Radio>
              </FormItemWrapper>
            </FormGroup>
          </SharedModalInputs>
        </SharedModalSecondContent>
        <SharedModalSecondContent>
          <HrWithHeader text={t('accordion.sample.sampleRequest')} />
        </SharedModalSecondContent>
        <SharedModalSecondContent>
          {!!samplesData?.length && (
            <div style={{ paddingRight: '20px' }}>
              <Card.Container disableShadow intent="warning" padding="8px 12px">
                <Text typography="accentMedium">{t('samples:sampleAlreadyExists')}</Text>
                <Text typography="bodyMedium" color="secondary">
                  <Trans
                    i18nKey="samples:sampleAlreadyExistsDetails"
                    values={{ samplesData: '' }}
                    components={[<>{samplesData}</>]}
                  />
                </Text>
              </Card.Container>
            </div>
          )}
          <SharedModalInputs>
            <CommonFormFields formik={formik} />
          </SharedModalInputs>
        </SharedModalSecondContent>
      </SharedModalContent>
    </ResponsiveModal>
  )
}
